import { useEffect, useMemo, useState } from "react";
import { Form, Modal } from "react-bootstrap";
import { Option } from "react-bootstrap-typeahead/types/types";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import IdentificationType from "../../../../../entities/IdentificationType";
import WorkerDocument from "../../../../../entities/WorkerDocument";
import { AppDispatch, AppState } from "../../../../../store/configureStore";
import { getIdentificationTypes } from "../../../../../store/slices/identificationTypes";
import ModalHeaderWithActions from "../../../../UI/ModalHeaderWithActions";
import TypeaheadWithIcon from "../../../../UI/TypeaheadWithIcon";
import "../ChangeDialogs.css";
import ProfileEditContentNode from "../ProfileEditContentNode";
import ProfileEditNode from "../ProfileEditNode";
import { parseUtcDate } from "util/dateUtils";
import ColumbusDatePickerInput from "pages/UI/ColumbusDatePickerInput";

interface TypeOption {
    id: number;
    label: string;
    type: IdentificationType;
}

interface Props {
    currentDocument: WorkerDocument | undefined;
    show: boolean;
    onClose: () => void;
    onSubmit: (data: WorkerDocument) => void;
}

const toDate = (value?: Date | string | null): Date | null => {
    if (!value) {
        return null;
    }
    const date = value instanceof Date ? value : new Date(value);
    return isNaN(date.getTime()) ? null : date;
};

const ChangeDocument = (props: Props) => {
    const { show, onClose, onSubmit } = props;
    const { t } = useTranslation("parts");
    const dispatch = useDispatch<AppDispatch>();

    const { list: identificationTypes } = useSelector(
        (state: AppState) => state.entities.identificationTypes
    );

    useEffect(() => {
        if (show && identificationTypes.length === 0) {
            dispatch(getIdentificationTypes());
        }
    }, [show, identificationTypes.length, dispatch]);

    const [selectedType, setSelectedType] = useState<IdentificationType | undefined>(undefined);
    const [value, setValue] = useState<string>("");
    const [validTo, setValidTo] = useState<Date | null>(null);

    const currentDate = useMemo(() => {
        const date = new Date();
        date.setHours(0, 0, 0, 0);
        return date;
    }, []);

    const minDate = useMemo(() => {
        const date = new Date();
        date.setFullYear(date.getFullYear() - 30); // Set minimum date to 30 years ago
        return date;
    }, []);

    useEffect(() => {
        if (show) {
            setSelectedType(
                props.currentDocument
                    ? identificationTypes.find(type => type.Id === props.currentDocument?.TypeId)
                    : undefined
            );
            setValue(props.currentDocument?.Value || "");
            setValidTo(toDate(props.currentDocument?.EndDate));
        }
    }, [show, props.currentDocument, identificationTypes]);

    const options: TypeOption[] = useMemo(
        () => identificationTypes
            .filter(type => typeof type.Name === "string" && type.Name.trim() !== "")
            .map(type => ({
                id: type.Id,
                label: type.Name,
                type
            })),
        [identificationTypes]
    );

    const handleTypeChange = (selected: Option[]) => {
        const typedSelected = selected as TypeOption[];
        setSelectedType(typedSelected.length > 0 ? typedSelected[0].type : undefined);
    };

    const canSubmit = useMemo(
        () => selectedType !== undefined && value.trim() !== "",
        [selectedType, value]
    );

    const toLocaleDateString = (date: Date): string => {
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, "0");
        const day = date.getDate().toString().padStart(2, "0");
        return `${year}-${month}-${day}`;
    }

    const handleSubmit = () => {
        if (!canSubmit || !selectedType) {
            return;
        }

        const document: WorkerDocument = {
            Id: props.currentDocument?.Id ?? 0,
            TypeId: selectedType.Id,
            TypeName: selectedType.Name,
            Value: value.trim(),
            EndDate: validTo ? toLocaleDateString(validTo) : null
        };

        onSubmit(document);
    };

    return show && (
        <Modal show={show} onHide={onClose} className="change-modal" enforceFocus={false}>
            <ModalHeaderWithActions
                title={t("changeDialog.document.changeDocument")}
                onClose={onClose}
                onSubmit={handleSubmit}
                canSubmit={canSubmit}
            />
            <Modal.Body>
                <Form>
                    <ProfileEditContentNode
                        label={t("contacts.documentType")}
                        mandatory={true}
                        hideClearButton={true}
                        handleClear={() => { }}
                    >
                        <TypeaheadWithIcon
                            id="formDocumentType"
                            onChange={handleTypeChange}
                            options={options}
                            selected={selectedType ? [{ id: selectedType.Id, label: selectedType.Name, type: selectedType }] : []}
                            placeholder={t("changeDialog.document.selectDocumentType")}
                            labelKey="label"
                            className="form-control-input-custom visual-selection-aid"
                        />
                    </ProfileEditContentNode>
                    <ProfileEditNode
                        controlId="formDocumentNumber"
                        label={t("contacts.documentNumber")}
                        mandatory={true}
                        placeholder={t("common:form.placeholders.enterData")}
                        value={value}
                        onChange={setValue}
                    />
                    <ProfileEditContentNode
                            label={`${t("contacts.documentValidTo")}:`}
                            hideClearButton={true}
                            handleClear={() => setValidTo(currentDate)}
                        >
                            <ColumbusDatePickerInput
                                className="form-control-input-custom"
                                value={validTo ? parseUtcDate(validTo) : null}
                                onChange={(date: Date | null) => {
                                    if (date) setValidTo(date);
                                }}
                                minDate={minDate}
                            />
                        </ProfileEditContentNode>
                </Form>
            </Modal.Body>
        </Modal>
    );
};

export default ChangeDocument;
